import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import {
  bookHolidayV2,
  CustomLeaveDays,
  CustomNonWorkingDays,
  deleteHolidayV2,
  fetchUsersHolidayInfoV2,
  getTeamHolidays,
  NonWorkingDays
} from '@features/holidays/services/HolidayService';
import {
  HolidayBookingRequest,
  HolidayDay,
  HolidayGrouping
} from "../pages/models/Holidays.model";
import moment, { Moment } from "moment";
import { failureNotification, warningNotification } from "@shared/store/notification.duck";
import { DATE_FORMAT, getListOfDaysBetweenInclusive } from "@shared/utils/DateUtils";
import { Period } from '@features/whereabouts/models/movements.models';
import { selectCalendarDate } from "@shared/store/dashboard.duck";
import { CompanyHoliday } from '@features/holidays/services/CompanyHolidayService';
import { groupBy } from "@shared/utils/ArrayUtils";
import { CustomWhereaboutsType } from '@features/whereabouts/services/WhereaboutOptions';
import { roundDays } from '@shared/utils/MathUtils';

export interface HolidaysV2State {
  usersHolidayBookings: HolidayDay[];
  usersHolidayGroupings: HolidayGrouping[];
  usersNonWorkingDays: NonWorkingDays[];
  usersCustomNonWorkingDays: CustomNonWorkingDays[];
  usersCustomLeave: CustomLeaveDays[];
  usersHolidayAllowance: number;
  carriedOverDays: number;
  additionalDays: number;
  totalAllowance: number;
  usersHolidaysTaken: number;
  companyHolidays: CompanyHoliday[];
  loading: boolean;
  loadError: boolean;
  loadErrorMessage?: string;
  teamHolidays: any[];
  selectedTeamIds: string[];
  selectedWorkGroupIds: number[];
  usersFirstName?: string;
  usersLastName?: string;
  usersTeamName?: string;
  usersUserId?: string;
}

export const initialHolidaysV2State: HolidaysV2State = {
  usersHolidayBookings: [],
  usersHolidayGroupings: [],
  usersNonWorkingDays: [],
  usersCustomNonWorkingDays: [],
  usersCustomLeave: [],
  companyHolidays: [],
  usersHolidayAllowance: 0,
  usersHolidaysTaken: 0,
  totalAllowance: 0,
  carriedOverDays: 0,
  additionalDays: 0,
  loading: false,
  loadError: false,
  teamHolidays: [],
  selectedTeamIds: [],
  selectedWorkGroupIds: [],
}

export const bookHoliday: any = createAsyncThunk(
  'holidaysV2/bookHoliday',
  async (holidayRequest: {fromDate?: Moment, fromDatePeriod: Period, toDate?: Moment, toDatePeriod: Period, userId?: string, originalFromDate?: Moment, originalToDate?: Moment, leaveType: number}, thunkAPI) => {
    const {fromDate, fromDatePeriod, toDate, toDatePeriod, userId, originalFromDate, originalToDate} = holidayRequest;
    if (!fromDate) {
      thunkAPI.dispatch(warningNotification('Start date required'));
      TrackJS?.track('Missing start date for holiday booking');
      return;
    }
    if (!toDate) {
      thunkAPI.dispatch(warningNotification('End date required'));
      TrackJS?.track('Missing end date for holiday booking');
      return;
    }
    if (!userId) {
      thunkAPI.dispatch(warningNotification('Failed to find user'));
      TrackJS?.track('Failed to find user to book holiday');
      return;
    }

    const dates = getListOfDaysBetweenInclusive(fromDate, toDate);
    const originalDates = getListOfDaysBetweenInclusive(originalFromDate, originalToDate).map(date => date.format(DATE_FORMAT));

    // Default all to AllDay, then update first and last after
    let datesToBookList = dates.map(date => new HolidayBookingRequest(date, userId, Period.AllDay));
    if (fromDatePeriod === Period.AM) datesToBookList[0].pm = false;
    else if (fromDatePeriod === Period.PM) datesToBookList[0].am = false;

    if (toDatePeriod === Period.AM) datesToBookList[datesToBookList.length - 1].pm = false;
    else if (toDatePeriod === Period.PM) datesToBookList[datesToBookList.length - 1].am = false;

    try {
      await bookHolidayV2({holidayBookingList: datesToBookList, originalDates: originalDates, leaveType: holidayRequest.leaveType});
      window.location.href = `/holidays/${userId}/success/${fromDate.format(DATE_FORMAT)}/${toDate.format(DATE_FORMAT)}`;
    } catch (e: any) {
      try {
        thunkAPI.dispatch(failureNotification(JSON.parse(e.message).message));
      } catch (e: any) {
        thunkAPI.dispatch(failureNotification('Failed to save holiday booking'));
      }
    }
  }
)

export const deleteHolidayBooking: any = createAsyncThunk(
  'holidaysV2/deleteHolidayBooking',
  async (holidayRequest: {fromDate?: Moment, fromDatePeriod: Period, toDate?: Moment, toDatePeriod: Period, userId?: string}, thunkAPI) => {
    const {fromDate, fromDatePeriod, toDate, toDatePeriod, userId} = holidayRequest;
    if (!fromDate) {
      thunkAPI.dispatch(warningNotification('Start date required'));
      TrackJS?.track('Missing start date for holiday booking');
      return;
    }
    if (!toDate) {
      thunkAPI.dispatch(warningNotification('End date required'));
      TrackJS?.track('Missing end date for holiday booking');
      return;
    }
    if (!userId) {
      thunkAPI.dispatch(warningNotification('Failed to find user'));
      TrackJS?.track('Failed to find user to book holiday');
      return;
    }

    const dates = getListOfDaysBetweenInclusive(fromDate, toDate);

    // Default all to AllDay, then update first and last after
    let datesToBookList = dates.map(date => new HolidayBookingRequest(date, userId, Period.AllDay));
    if (fromDatePeriod === Period.AM) datesToBookList[0].pm = false;
    else if (fromDatePeriod === Period.PM) datesToBookList[0].am = false;

    if (toDatePeriod === Period.AM) datesToBookList[datesToBookList.length - 1].pm = false;
    else if (toDatePeriod === Period.PM) datesToBookList[datesToBookList.length - 1].am = false;

    await deleteHolidayV2({holidayBookingList: datesToBookList});
    window.location.href = '/holidays?v=user';
  }
)

const GENERIC_HOLIDAY_LOAD_ERROR = 'We couldn\'t load these holidays. Please refresh the page or try again later.';

// Map known server error messages to friendlier, user-facing copy.
const HOLIDAY_LOAD_ERROR_MESSAGES: Record<string, string> = {
  'Not allowed to read users holiday info': "You don't have permission to view this person's holidays.",
};

function toHolidayLoadErrorMessage(rawMessage?: string): string {
  if (!rawMessage) {
    return GENERIC_HOLIDAY_LOAD_ERROR;
  }
  // The agent may surface the server error as raw JSON (e.g. {"message":"..."}),
  // so extract the inner message before matching/displaying it.
  let serverMessage = rawMessage;
  try {
    const parsed = JSON.parse(rawMessage);
    if (parsed && typeof parsed.message === 'string') {
      serverMessage = parsed.message;
    }
  } catch (e) {
    // not JSON — use the raw message as-is
  }
  return HOLIDAY_LOAD_ERROR_MESSAGES[serverMessage] || serverMessage;
}

export const loadUsersHolidays: any = createAsyncThunk(
  'holidaysV2/loadUsersHolidays',
  async (params: {userId: string, holidayYear: Moment}, thunkAPI) => {
    const calendarDate = (thunkAPI.getState() as AppState).dashboard.calendarDate;
    const config = (thunkAPI.getState() as AppState).config.configEntity;
    const holidayStartDate = config.getHolidayYearStartDate(!!params.holidayYear ? params.holidayYear : calendarDate);
    let response;
    try {
      response = await fetchUsersHolidayInfoV2(holidayStartDate.format(DATE_FORMAT), params.userId);
    } catch (e: any) {
      // The agent's processError surfaces the server's message on e.message (HttpError).
      // Known messages are mapped to friendlier copy; otherwise fall back to a generic message.
      return thunkAPI.rejectWithValue(toHolidayLoadErrorMessage(e?.message));
    }
    return {
      usersFirstName: response.firstName,
      usersLastName: response.lastName,
      usersTeamName: response.teamName,
      usersUserId: response.userId,
      usersHolidayBookings: response.holidayBookings,
      usersHolidayAllowance: response.holidayAllowance,
      carriedOverDays: response.carriedOverDays,
      additionalDays: response.additionalDays,
      totalAllowance: response.totalAllowance,
      usersHolidaysTaken: response.holidaysTaken,
      usersHolidayGroupings: response.holidayGroupings,
      companyHolidays: response.companyHolidays,
      usersNonWorkingDays: response.nonWorkingDays,
      usersCustomNonWorkingDays: response.customNonWorkingDays,
      usersCustomLeave: response.customLeaveDays,
    }
  }
);

export const loadTeamHolidays: any = createAsyncThunk(
  'holidaysV2/loadTeamHolidays',
  async (params: {teamIds?: string[], workGroupIds?: number[], departmentIds?: number[], date?: Moment}, thunkAPI) => {
    const date = params?.date ?? selectCalendarDate(thunkAPI.getState() as AppState);
    if (!date) {
      throw new Error('Calendar date not set');
    }

    const workGroupIds = params?.workGroupIds ?? [];
    const teamIds = params?.teamIds ?? [];
    const departmentIds = params?.departmentIds ?? [];

    const appState = (thunkAPI.getState() as AppState);
    const selectedTeamIds = appState.holidaysV2?.selectedTeamIds ?? [];
    const selectedWorkGroupIds = appState.holidaysV2?.selectedWorkGroupIds ?? [];

    if (workGroupIds.length > 0 || teamIds.length > 0 || departmentIds.length > 0) {
      thunkAPI.dispatch(setSelectedGroupIds({teamIds: teamIds, workGroupIds: workGroupIds}))
      return await getTeamHolidays(teamIds, workGroupIds, date, departmentIds);
    } else if (selectedTeamIds.length > 0 || selectedWorkGroupIds.length > 0) {
      return await getTeamHolidays(selectedTeamIds,  selectedWorkGroupIds, date);
    } else {
      thunkAPI.dispatch(failureNotification('No team selected'));
      throw new Error('No team id selected')
    }
  }
);


const holidaysV2Slice: any = createSlice({
  name: 'holidaysV2',
  initialState: initialHolidaysV2State,
  reducers: {
    setSelectedTeamIds: (state, action) => ({...state, selectedTeamIds: action.payload, selectedWorkGroupIds: []}),
    setSelectedWorkgroupIds: (state, action) => ({...state, selectedTeamIds: [], selectedWorkGroupIds: action.payload}),
    setSelectedGroupIds: (state, action) => ({
      ...state,
      selectedTeamIds: action.payload.teamIds,
      selectedWorkGroupIds: action.payload.workGroupIds,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUsersHolidays.pending, (state) => ({
      ...state,
      usersHolidayBookings: [],
      usersHolidayGroupings: [],
      usersCustomLeave: [],
      usersNonWorkingDays: [],
      usersCustomNonWorkingDays: [],
      companyHolidays: [],
      usersHolidayAllowance: 0,
      carriedOverDays: 0,
      additionalDays: 0,
      usersHolidaysTaken: 0,
      loading: true,
      loadError: false,
      loadErrorMessage: undefined,
    }))
      .addCase(loadUsersHolidays.rejected, (state, action) => ({...state, loading: false, loadError: true, loadErrorMessage: action.payload as string, }))
      .addCase(loadUsersHolidays.fulfilled, (state, action) => ({
      ...state,
      ...action.payload,
      loading: false,
      loadError: false,
      loadErrorMessage: undefined,
    }))
      .addCase(deleteHolidayBooking.pending, (state) => ({...state, loading: true, }))
      .addCase(deleteHolidayBooking.rejected, (state) => ({...state, loading: false, }))
      .addCase(deleteHolidayBooking.fulfilled, (state) => ({...state, loading: false, }))
      .addCase(bookHoliday.pending, (state) => ({...state, loading: true, }))
      .addCase(bookHoliday.rejected, (state) => ({...state, loading: false, }))
      .addCase(bookHoliday.fulfilled, (state) => ({...state, loading: false, }))
      .addCase(loadTeamHolidays.pending, (state) => ({...state, loading: true, teamHolidays: []}))
      .addCase(loadTeamHolidays.rejected, (state) => ({...state, loading: false, teamHolidays: []}))
      .addCase(loadTeamHolidays.fulfilled, (state, action) => ({...state, loading: false, teamHolidays: action.payload}));
  }
});

const { setSelectedGroupIds } = holidaysV2Slice.actions;
export default holidaysV2Slice.reducer;

// Selectors
export const selectUsersBookedHolidays = (state: AppState) =>  state.holidaysV2.usersHolidayBookings;
export const selectUsersCustomLeave = (state: AppState) =>  state.holidaysV2.usersCustomLeave;
export const selectUsersNonWorkingDay = (state: AppState) =>  state.holidaysV2.usersNonWorkingDays;
export const selectUsersCustomNonWorkingDay = (state: AppState) =>  state.holidaysV2.usersCustomNonWorkingDays;
export const selectCompanyHolidays = (state: AppState) =>  state.holidaysV2.companyHolidays;
export const selectUsersHolidayGroupings = (state: AppState) => {
  return state.holidaysV2.usersHolidayGroupings
};
const selectUsersUpcomingHolidayGroupings = (state: AppState) => {
  const upcoming = (state.holidaysV2.usersHolidayGroupings ?? []).filter(grouping => !moment(grouping.toDate).isBefore(moment().subtract(1, 'days')))
  return upcoming.sort((a, b) => a.fromDate < b.fromDate ? -1 : 1)
}
export const selectUsersHolidayInfo = (state: AppState) =>  {
  const customLeave = state.holidaysV2.usersCustomLeave.flatMap(cl => [{id: cl.amWhereaboutsId}, {id: cl.pmWhereaboutsId}]);
  const groupedIds = groupBy(customLeave, 'id');

  const options = (state.whereaboutsOptions.whereaboutsOptions ?? [])
    .filter(wo => wo.whereaboutsType === CustomWhereaboutsType.LEAVE)
    .map(wo => ({key: wo.key, label: wo.label, count: groupedIds[wo.id] ? groupedIds[wo.id].length / 2 : 0}))
    .filter(wo => wo.count > 0);

  // state.holidaysV2.usersHolidayAllowance holds the TOTAL allowance from the API
  // (base + additional + carried over). daysLeft is intentionally derived from that
  // total. The basic allowance is the total minus the additional and carried-over days.
  const basicHolidayAllowance = roundDays(state.holidaysV2.usersHolidayAllowance
    - state.holidaysV2.additionalDays
    - state.holidaysV2.carriedOverDays);

  return {
    daysTaken: state.holidaysV2.usersHolidaysTaken,
    daysLeft: roundDays(state.holidaysV2.usersHolidayAllowance - state.holidaysV2.usersHolidaysTaken),
    customLeaveDays: options,
    usersHolidayAllowance: basicHolidayAllowance,
    additionalDays: state.holidaysV2.additionalDays,
    carriedOverDays: state.holidaysV2.carriedOverDays,
    totalAllowance: state.holidaysV2.totalAllowance,
    totalAllowanceIncludingAllCarriedOverLeave: state.holidaysV2.totalAllowance,
  }
};
export const selectMonthlyHolidayView = (state: AppState) =>  state.holidaysV2.teamHolidays;
export const selectIsLoading = (state: AppState) => state.holidaysV2.loading;
export const selectHasHolidayLoadError = (state: AppState) => state.holidaysV2.loadError;
export const selectHolidayLoadErrorMessage = (state: AppState) => state.holidaysV2.loadErrorMessage;
export const selectHolidayUserInfo = (state: AppState) => ({
  firstName: state.holidaysV2.usersFirstName,
  lastName: state.holidaysV2.usersLastName,
  teamName: state.holidaysV2.usersTeamName,
  userId: state.holidaysV2.usersUserId,
})
